import type { ChatMessage } from '@manaro/contracts'

const DAY_MS = 24 * 60 * 60 * 1000
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' })
const DAY_MONTH_YEAR = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/** «Сегодня», «Вчера» or a date — the separator between days in a conversation. */
export function dayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS)
  if (days === 0) return 'Сегодня'
  if (days === 1) return 'Вчера'
  return (date.getFullYear() === now.getFullYear() ? DAY_MONTH : DAY_MONTH_YEAR).format(date)
}

export interface ChatRow {
  message: ChatMessage
  /** A date separator goes before this message. */
  day: string | null
  /** First of several messages in a row from the same author: show who wrote it. */
  firstInRun: boolean
  /** Last of the run: the bubble gets its tail and a little more space below. */
  lastInRun: boolean
}

const authorKey = (message: ChatMessage): string =>
  message.role === 'operator' ? `operator:${message.authorName ?? ''}` : message.role

/** Lays messages out like a messenger: day separators and runs of messages from one author. */
export function layoutMessages(messages: ChatMessage[], now: Date = new Date()): ChatRow[] {
  return messages.map((message, index) => {
    const previous = messages[index - 1]
    const next = messages[index + 1]
    const day = dayLabel(message.createdAt, now)
    const newDay = !previous || dayLabel(previous.createdAt, now) !== day
    const sameAsPrevious = !newDay && previous?.role !== 'system' && previous && authorKey(previous) === authorKey(message)
    const nextNewDay = !next || dayLabel(next.createdAt, now) !== day
    const sameAsNext = !nextNewDay && next?.role !== 'system' && next && authorKey(next) === authorKey(message)
    return { message, day: newDay ? day : null, firstInRun: !sameAsPrevious, lastInRun: !sameAsNext }
  })
}

export type TextPart = { kind: 'text'; value: string } | { kind: 'link'; value: string }

const URL_PATTERN = /https?:\/\/[^\s<>«»"]+/g
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/

/** Splits a message into plain text and links, so URLs the bot sends can be opened with a tap. */
export function splitLinks(text: string): TextPart[] {
  const parts: TextPart[] = []
  let cursor = 0
  for (const match of text.matchAll(URL_PATTERN)) {
    const raw = match[0]
    const url = raw.replace(TRAILING_PUNCTUATION, '')
    const start = match.index ?? 0
    if (start > cursor) parts.push({ kind: 'text', value: text.slice(cursor, start) })
    parts.push({ kind: 'link', value: url })
    cursor = start + url.length
  }
  if (cursor < text.length) parts.push({ kind: 'text', value: text.slice(cursor) })
  return parts
}

/** Close enough to the end that new messages should scroll into view on their own. */
export const NEAR_BOTTOM_PX = 120

export function isNearBottom(element: Pick<HTMLElement, 'scrollHeight' | 'scrollTop' | 'clientHeight'>): boolean {
  return element.scrollHeight - element.scrollTop - element.clientHeight < NEAR_BOTTOM_PX
}
